import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FiDollarSign, FiAlertCircle, FiCheckCircle, FiClock, FiEdit, FiFilter } from 'react-icons/fi';
import './ContasReceber.css';

const ContasReceber = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  
  // Permission check
  const hasPermission = userData?.permissions?.includes('Master') || 
                       userData?.permissions?.includes('Diretoria') ||
                       userData?.permissions?.includes('Financeiro');

  // State
  const [loading, setLoading] = useState(true);
  const [regularContas, setRegularContas] = useState([]);
  const [reembolsoContas, setReembolsoContas] = useState([]);
  const [regularSummary, setRegularSummary] = useState({
    totalPendente: 0,
    totalVencido: 0,
    totalAVencer: 0,
    count: 0,
    countVencidas: 0
  });
  const [reembolsoSummary, setReembolsoSummary] = useState({
    totalPendente: 0,
    totalVencido: 0,
    totalAVencer: 0,
    count: 0,
    countVencidas: 0
  });
  const [regularPagination, setRegularPagination] = useState({});
  const [reembolsoPagination, setReembolsoPagination] = useState({});
  
  // Separate filters for each list
  const [regularFilters, setRegularFilters] = useState({
    status: '',
    clienteId: '',
    startDate: '',
    endDate: ''
  });
  
  const [reembolsoFilters, setReembolsoFilters] = useState({
    status: '',
    clienteId: '',
    startDate: '',
    endDate: ''
  });
  
  const [showRecebimentoModal, setShowRecebimentoModal] = useState(false);
  const [selectedConta, setSelectedConta] = useState(null);
  const [recebimentoForm, setRecebimentoForm] = useState({
    valorRecebido: '',
    dataRecebimento: new Date().toISOString().split('T')[0],
    observacoes: '',
    itemType: 'regular',
    itemId: null
  });
  
  // Separate current pages
  const [regularCurrentPage, setRegularCurrentPage] = useState(1);
  const [reembolsoCurrentPage, setReembolsoCurrentPage] = useState(1);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('regular');

  // Currency formatter
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Date formatter
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  // Fetch contas a receber
  const fetchContasReceber = async () => {
    try {
      setLoading(true);
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : 'http://localhost:5000';
      
      const queryParams = new URLSearchParams({
        page: activeTab === 'regular' ? regularCurrentPage : reembolsoCurrentPage,
        limit: 20,
        type: 'both',
        ...(activeTab === 'regular' ? regularFilters : reembolsoFilters)
      });

      const response = await fetch(
        `${API_URL}/api/contas-receber?${queryParams}`,
        { 
          headers: { 
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Erro ao carregar contas a receber');
      }

      const data = await response.json();
      console.log('API Response:', data);
      
      // Check if data structure is correct
      if (data.data && data.data.regular && data.data.reembolsos) {
        // Set regular data
        setRegularContas(data.data.regular.contas || []);
        setRegularSummary(data.data.regular.summary || {
          totalPendente: 0,
          totalVencido: 0,
          totalAVencer: 0,
          count: 0,
          countVencidas: 0
        });
        setRegularPagination(data.data.regular.pagination || {});
        
        // Set reimbursement data
        setReembolsoContas(data.data.reembolsos.contas || []);
        setReembolsoSummary(data.data.reembolsos.summary || {
          totalPendente: 0,
          totalVencido: 0,
          totalAVencer: 0,
          count: 0,
          countVencidas: 0
        });
        setReembolsoPagination(data.data.reembolsos.pagination || {});
      } else {
        console.error('Unexpected data structure:', data);
        // Handle old API structure if needed
        if (data.data && data.data.contas) {
          // This is the old structure, we need to update backend
          alert('API retornando estrutura antiga. Por favor, reinicie o servidor backend.');
        }
      }
      
    } catch (error) {
      console.error('Error fetching contas receber:', error);
      alert('Erro ao carregar dados: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle recebimento
  const handleRecebimento = async () => {
    try {
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_VERCEL_API_URL 
        : 'http://localhost:5000';

      const response = await fetch(
        `${API_URL}/api/contas-receber/${selectedConta._id}/recebimento`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            valorRecebido: parseFloat(recebimentoForm.valorRecebido),
            dataRecebimento: recebimentoForm.dataRecebimento,
            observacoes: recebimentoForm.observacoes,
            itemType: recebimentoForm.itemType,
            itemId: recebimentoForm.itemId
          })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Erro ao registrar recebimento');
      }

      alert('Recebimento registrado com sucesso!');
      setShowRecebimentoModal(false);
      setSelectedConta(null);
      setRecebimentoForm({
        valorRecebido: '',
        dataRecebimento: new Date().toISOString().split('T')[0],
        observacoes: '',
        itemType: 'regular',
        itemId: null
      });
      fetchContasReceber();
    } catch (error) {
      console.error('Error registering payment:', error);
      alert('Erro ao registrar recebimento: ' + error.message);
    }
  };

  // Open recebimento modal
  const openRecebimentoModal = (conta) => {
    setSelectedConta(conta);
    setRecebimentoForm({
      valorRecebido: conta.valorPendente.toString(),
      dataRecebimento: new Date().toISOString().split('T')[0],
      observacoes: '',
      itemType: conta.type === 'reembolso' ? 'reembolso' : 'regular',
      itemId: conta.type === 'reembolso' && conta.reembolsos && conta.reembolsos[0] ? conta.reembolsos[0].id : null
    });
    setShowRecebimentoModal(true);
  };

  useEffect(() => {
    if (!hasPermission) {
      alert('Você não tem permissão para acessar esta página.');
      navigate('/painel');
      return;
    }
    
    fetchContasReceber();
  }, [regularCurrentPage, reembolsoCurrentPage, regularFilters, reembolsoFilters, hasPermission, navigate]);

  if (!hasPermission) {
    return null;
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  // Render summary cards
  const renderSummaryCards = (summary, type) => (
    <div className="summary-cards">
      <div className="summary-card">
        <div className="summary-icon">
          <FiDollarSign />
        </div>
        <div className="summary-content">
          <h4>Total Pendente {type === 'reembolso' ? '(Reembolsos)' : ''}</h4>
          <p className="summary-value">{formatCurrency(summary.totalPendente)}</p>
          <span className="summary-detail">{summary.count} contas</span>
        </div>
      </div>

      <div className="summary-card vencidas">
        <div className="summary-icon">
          <FiAlertCircle />
        </div>
        <div className="summary-content">
          <h4>Vencidas</h4>
          <p className="summary-value">{formatCurrency(summary.totalVencido)}</p>
          <span className="summary-detail">{summary.countVencidas} contas</span>
        </div>
      </div>

      <div className="summary-card a-vencer">
        <div className="summary-icon">
          <FiClock />
        </div>
        <div className="summary-content">
          <h4>A Vencer</h4>
          <p className="summary-value">{formatCurrency(summary.totalAVencer)}</p>
          <span className="summary-detail">{summary.count - summary.countVencidas} contas</span>
        </div>
      </div>
    </div>
  );

  // Render filters
  const renderFilters = (filters, setFilters, type) => (
    <div className="filters-section">
      <div className="filter-group">
        <label>Status:</label>
        <select 
          value={filters.status} 
          onChange={(e) => setFilters({...filters, status: e.target.value})}
        >
          <option value="">Todos</option>
          <option value="vencidas">Vencidas</option>
          <option value="a_vencer">A Vencer</option>
          <option value="proximos_30_dias">Próximos 30 dias</option>
        </select>
      </div>

      <div className="filter-group">
        <label>Data Inicial:</label>
        <input 
          type="date" 
          value={filters.startDate}
          onChange={(e) => setFilters({...filters, startDate: e.target.value})}
        />
      </div>

      <div className="filter-group">
        <label>Data Final:</label>
        <input 
          type="date" 
          value={filters.endDate}
          onChange={(e) => setFilters({...filters, endDate: e.target.value})}
        />
      </div>

      <button 
        className="clear-filters-button"
        onClick={() => setFilters({ status: '', clienteId: '', startDate: '', endDate: '' })}
      >
        Limpar Filtros
      </button>
    </div>
  );

  // Render table
  const renderTable = (contas, type) => {
    console.log(`Rendering ${type} table with ${contas.length} items:`, contas);
    return (
      <div className="table-container">
        <table className="contas-table">
          <thead>
            <tr>
              <th>Título</th>
              <th>Cliente</th>
              <th>Responsável</th>
              <th>Valor Total</th>
              <th>Valor Recebido</th>
              <th>Valor Pendente</th>
              <th>Vencimento</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {contas && contas.length > 0 ? (
              contas.map(conta => (
                <tr key={`${conta._id}-${type}`} className={conta.vencida ? 'vencida' : ''}>
                  <td>{conta.titulo}</td>
                  <td>{conta.cliente?.nome || conta.cliente?.email || 'N/A'}</td>
                  <td>{conta.responsavel?.name || conta.responsavel?.email || 'N/A'}</td>
                  <td>{formatCurrency(conta.valorTotal)}</td>
                  <td>{formatCurrency(conta.valorRecebido)}</td>
                  <td className="valor-pendente">{formatCurrency(conta.valorPendente)}</td>
                  <td>
                    {formatDate(conta.dataVencimento)}
                    {conta.diasAtraso > 0 && (
                      <span className="dias-atraso"> ({conta.diasAtraso} dias)</span>
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${conta.vencida ? 'vencida' : 'a-vencer'}`}>
                      {conta.vencida ? 'Vencida' : 'A Vencer'}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="action-button receber"
                      onClick={() => openRecebimentoModal(conta)}
                      title="Registrar Recebimento"
                    >
                      <FiCheckCircle />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>
                  Nenhuma conta a receber encontrada.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Render pagination
  const renderPagination = (pagination, currentPage, setCurrentPage) => (
    pagination.totalPages > 1 && (
      <div className="pagination">
        <button 
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          Anterior
        </button>
        <span>Página {pagination.currentPage} de {pagination.totalPages}</span>
        <button 
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === pagination.totalPages}
        >
          Próxima
        </button>
      </div>
    )
  );

  return (
    <div className="contas-receber">
      <div className="contas-receber-inner">
        {/* Header */}
        <div className="page-header">
          <h2>Contas a Receber</h2>
        </div>

        {/* Tabs */}
        <div className="tabs-container" style={{ display: 'flex', flexDirection: 'row' }}>
          <button 
            className={`tab ${activeTab === 'regular' ? 'active' : ''}`}
            onClick={() => setActiveTab('regular')}
            style={{ display: 'inline-block' }}
          >
            Serviços e Atividades
          </button>
          <button 
            className={`tab ${activeTab === 'reembolso' ? 'active' : ''}`}
            onClick={() => setActiveTab('reembolso')}
            style={{ display: 'inline-block' }}
          >
            Reembolsos
          </button>
        </div>

        {/* Content Container */}
        <div className="content-container">
          {/* Regular Items Section */}
          {activeTab === 'regular' && (
            <div className="regular-section">
              {renderSummaryCards(regularSummary, 'regular')}
              {renderFilters(regularFilters, setRegularFilters, 'regular')}
              {renderTable(regularContas, 'regular')}
              {renderPagination(regularPagination, regularCurrentPage, setRegularCurrentPage)}
            </div>
          )}

          {/* Reimbursement Items Section */}
          {activeTab === 'reembolso' && (
            <div className="reembolso-section">
              {renderSummaryCards(reembolsoSummary, 'reembolso')}
              {renderFilters(reembolsoFilters, setReembolsoFilters, 'reembolso')}
              {renderTable(reembolsoContas, 'reembolso')}
              {renderPagination(reembolsoPagination, reembolsoCurrentPage, setReembolsoCurrentPage)}
            </div>
          )}
        </div>

      {/* Recebimento Modal */}
      {showRecebimentoModal && (
        <div className="modal-overlay" onClick={() => setShowRecebimentoModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Registrar Recebimento</h3>
            
            <div className="modal-info">
              <p><strong>Título:</strong> {selectedConta.titulo}</p>
              <p><strong>Cliente:</strong> {selectedConta.cliente?.nome}</p>
              <p><strong>Valor Pendente:</strong> {formatCurrency(selectedConta.valorPendente)}</p>
              {selectedConta.type === 'reembolso' && (
                <p><strong>Tipo:</strong> Reembolso</p>
              )}
            </div>

            <div className="form-group">
              <label>Valor Recebido:</label>
              <input 
                type="number" 
                step="0.01"
                value={recebimentoForm.valorRecebido}
                onChange={(e) => setRecebimentoForm({...recebimentoForm, valorRecebido: e.target.value})}
                max={selectedConta.valorPendente}
              />
            </div>

            <div className="form-group">
              <label>Data do Recebimento:</label>
              <input 
                type="date" 
                value={recebimentoForm.dataRecebimento}
                onChange={(e) => setRecebimentoForm({...recebimentoForm, dataRecebimento: e.target.value})}
              />
            </div>

            <div className="form-group">
              <label>Observações:</label>
              <textarea 
                value={recebimentoForm.observacoes}
                onChange={(e) => setRecebimentoForm({...recebimentoForm, observacoes: e.target.value})}
                rows="3"
              />
            </div>

            <div className="modal-actions">
              <button className="cancel-button" onClick={() => setShowRecebimentoModal(false)}>
                Cancelar
              </button>
              <button 
                className="submit-button" 
                onClick={handleRecebimento}
                disabled={!recebimentoForm.valorRecebido || parseFloat(recebimentoForm.valorRecebido) <= 0}
              >
                Registrar Recebimento
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ContasReceber;
